/**
 * MSAL (Microsoft Authentication Library) configuration.
 *
 * Reads Azure AD app-registration details from environment variables
 * prefixed with REACT_APP_ (Create React App convention).
 *
 * Group-based access control helpers inspect the ID-token claims that
 * Azure AD includes when the app registration has "groupMembershipClaims"
 * set to "SecurityGroup" in its manifest.
 */

import { PublicClientApplication } from '@azure/msal-browser';

// ── MSAL Config ──────────────────────────────────────────────────

const msalConfig = {
  auth: {
    clientId: process.env.REACT_APP_AZURE_CLIENT_ID || '',
    authority: `https://login.microsoftonline.com/${process.env.REACT_APP_AZURE_TENANT_ID || 'common'}`,
    redirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
};

/** Singleton MSAL instance shared across the app */
export const msalInstance = new PublicClientApplication(msalConfig);

// ── Login request scopes ─────────────────────────────────────────

export const loginRequest = {
  scopes: ['openid', 'profile', 'email'],
};

// ── Group helpers ────────────────────────────────────────────────

/**
 * Extract the `groups` array from the active account's ID-token claims.
 * Returns an empty array if no account is signed in or no groups claim exists.
 */
export function getGroups() {
  const accounts = msalInstance.getAllAccounts();
  if (!accounts || accounts.length === 0) return [];
  const claims = accounts[0].idTokenClaims;
  return claims?.groups || [];
}

/**
 * Check whether the signed-in user belongs to a specific Entra security group.
 * @param {string} groupId - The object-ID of the Entra group to check.
 */
export function hasGroupAccess(groupId) {
  if (!groupId) return false;
  return getGroups().includes(groupId);
}

/**
 * Check whether the signed-in user is a dashboard admin.
 * Admin group ID is read from the REACT_APP_DASHBOARD_ADMIN_GROUP env var.
 */
export function isAdmin() {
  const adminGroupId = process.env.REACT_APP_DASHBOARD_ADMIN_GROUP;
  if (!adminGroupId) return false;
  return hasGroupAccess(adminGroupId);
}
